<template>
  <div class="department-user-selector">
    <div class="selector-columns flex gap-10">
      <!-- 왼쪽 영역: 부서 트리 -->
      <div class="column">
        <div class="column-header">
          <h3 class="column-title">{{ t('common.userSelect.department') }}</h3>
          <div class="tree-actions">
            <button class="tree-action-btn" @click="expandAllDepts">
              <i class="icon icon-sm icon-plus"></i>
              {{ t('common.userSelect.expandAll') }}
            </button>
            <button class="tree-action-btn" @click="collapseAllDepts">
              <i class="icon icon-sm icon-minus"></i>
              {{ t('common.userSelect.collapseAll') }}
            </button>
          </div>
        </div>

        <!-- 재귀 컴포넌트인 DepartmentTreeItem을 통해 트리 구성 -->
        <div class="department-tree">
          <DepartmentTreeItem
            v-for="dept in deptList.filter(d => !d.upDeptId)"
            :key="getFullDeptId(dept) || dept.deptId"
            :dept="dept"
            :dept-list="deptList"
            :selected-dept="selectedDept"
            :expanded-depts="expandedDepts"
            @select-dept="selectDept"
            @toggle-dept="toggleDept"
          />
        </div>
      </div>

      <!-- 오른쪽 영역: 사용자 목록 -->
      <div class="column">
        <div class="column-header">
          <h3 class="column-title">{{ t('common.userSelect.users') }}</h3>
        </div>
        <Empty
          v-if="filteredUsers.length === 0"
          iconType="search"
          :title="t('common.userSelect.noResults')"
          :description="t('common.userSelect.tryDifferentKeyword')"
        />
        <div v-else class="user-list">
          <div
            v-for="user in filteredUsers"
            :key="user.userId"
            class="user-item"
            :class="{ selected: selectedUsers.includes(user.userId) }"
            @click="selectUser(user)"
          >
            <div class="user-info">
              <span class="user-name">{{ user.userNm }}</span>
              <span v-if="showUserId && user.userId" class="user-id">{{ user.userId }}</span>
              <span class="user-rank">{{ user.jikgubNm }}</span>
            </div>
            <div class="user-department">{{ user.deptNm }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import DepartmentTreeItem from '~/components/user/DepartmentTreeItem.vue';
import { computed, onMounted } from 'vue';
import { useUserSelectorApi } from '~/composables/useUserSelectorApi.js';
import { useUserSelectorStore } from '~/composables/useUserSelectorStore.js';

const { expandedDepts, selectedDept, deptList, users, selectedUsers, selectedUserObject, showUserId } = useUserSelectorStore();
const { fetchDeptList, fetchDeptInsaList, fetchUserList, fetchUserInsaList } = useUserSelectorApi();
const { userInfo } = useUserStore();
const { t } = useI18n();
const props = defineProps({
  findYear: {
    type: String,
    default: '',
  },
  evalCycleCd: {
    type: String,
    default: '',
  },
});

// 컴포넌트 마운트 시 부서 목록 조회
// TODO findCompId
onMounted(async () => {
  try {
    const shouldUseDeptInsaList = !!props.findYear && !!props.evalCycleCd;
    const res = shouldUseDeptInsaList
      ? await fetchDeptInsaList({
          findYear: props.findYear,
          evalCycleCd: props.evalCycleCd,
        })
      : await fetchDeptList({ findCompId: userInfo.value.compId });

    deptList.value = res.list;
    applyInitialDeptExpansion();
  } catch (err) {
    console.error('부서 목록 조회 실패', err);
  }
});

// 부서 선택
// TODO findYear 추후 변경
const selectDept = async deptId => {
  selectedDept.value = deptId;

  try {
    const shouldUseDeptInsaList = !!props.findYear && !!props.evalCycleCd;
    const res = shouldUseDeptInsaList
      ? await fetchUserInsaList({
          findYear: props.findYear,
          deptId: deptId,
          userNm: '',
          evalCycleCd: props.evalCycleCd,
          userIdYn: showUserId.value ? 'Y' : 'N',
        })
      : await fetchUserList({
          compId: userInfo.value.compId,
          deptId: deptId,
          findYear: '2024',
        });

    users.value = res.list;
  } catch (err) {
    console.error('부서 선택에 따른 사용자 조회 실패', err);
  }
};

// 부서 열고 닫는 함수
const toggleDept = deptId => {
  const index = expandedDepts.value.indexOf(deptId);
  if (index === -1) expandedDepts.value.push(deptId);
  else expandedDepts.value.splice(index, 1);
};

// fullDeptId(전체 경로) 필드를 방어적으로 읽는다. (트리 key 중복 방지용)
const getFullDeptId = dept => String(dept?.fullDeptId ?? dept?.full_dept_id ?? dept?.fullDeptPath ?? '');

const filteredUsers = computed(() => {
  if (!selectedDept.value) return users.value;
  return users.value.filter(user => user.deptId === selectedDept.value);
});

// 사용자 선택 처리
const selectUser = user => {
  selectedUsers.value = [user.userId];
  selectedUserObject.value = user;
};

// JSP와 동일하게 1레벨 비말단 조직만 초기 펼침
const applyInitialDeptExpansion = () => {
  expandedDepts.value = deptList.value
    .filter(dept => String(dept?.deptLevel ?? '') === '1' && String(dept?.isLeaf ?? 'N') === 'N')
    .map(dept => dept.deptId);
};

// 부서 전체 펼침
const expandAllDepts = () => {
  expandedDepts.value = deptList.value
    .filter(dept => String(dept?.isLeaf ?? 'N') === 'N')
    .map(dept => dept.deptId);
};

// 부서 전체 닫힘
const collapseAllDepts = () => {
  expandedDepts.value = [];
};

defineExpose({ selectedUserObject, users, expandAllDepts, selectedDept });
</script>

<style lang="scss" scoped>
.ui-popup--fullscreen .department-user-selector{
  height: calc(100% - 50px);
}
.department-user-selector {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 500px;
  border-radius: 4px;
  overflow: hidden;
}

.selector-columns {
  display: flex;
  width: 100%;
  height: 100%;
}

.column {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  min-height: 400px;
}

.column-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.column-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.tree-actions {
  display: flex;
  gap: 8px;
}

.tree-action-btn {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  font-size: 12px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }

  .icon {
    margin-right: 4px;
  }
}

.department-tree {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.dept-item-wrapper {
  margin-bottom: 4px;
}

.dept-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &.selected {
    background-color: rgba(255, 107, 53, 0.15);
  }

  .dept-name {
    font-weight: 500;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  &.hasChild {
    font-weight: 500;
  }

}

.dept-toggle-icon {
  margin-right: 8px;
  color: #666;
}

.sub-departments {
  margin-left: 24px;
  margin-top: 4px;

  .dept-item:not(.hasChild) {
    &::before {
      content: '- ';
      display: inline-block;
      margin-right: 4px;
      color: #666;
    }
  }
}

.user-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }

  &.selected {
    background-color: rgba(40, 167, 69, 0.1);
    border: 1px solid #28a745;
    color: #155724;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-name {
  font-weight: 500;
}

.user-id {
  font-size: 12px;
  color: #888;
  margin-left: 4px;
}

.user-rank {
  font-size: 12px;
  color: #666;
  margin-left: 8px;
}

/* 이동 버튼 영역 스타일 */
.transfer-buttons-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 60px;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
}

.transfer-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.transfer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  font-size: 20px;

  &:hover {
    background-color: #f0f0f0;
  }

  &.transfer-right {
    color: #0066cc;
  }

  &.transfer-left {
    color: #0066cc;
  }
}
</style>
